import { AnyAction } from "redux";
import { SagaIterator } from "redux-saga";
import { Action } from "typescript-fsa";
import { call, put, select, takeLatest } from "typed-redux-saga";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { JsonRpc2Response } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { Events, ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentModel } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { Localizable } from "@com.mgmtp.a12.utils/utils-localization/lib/main";

import { saveActivity } from "../../../utilities/activityUtils";
import { RESOURCE_KEYS } from "../../../localization";
import { EPaymentStartRequestDto } from "../../../types/epayment";
import { assertNonNullable } from "../../../utilities/assert";
import { putErrorNotification, putSuccessNotification } from "../../../utilities/notificationUtils";
import { getEPaymentStatus, isJsonRpc2ResponseArray, startEPayment } from "../../../utilities/rpcHelper";
import { ModulfUiSelectors } from "../../../utilities/selectors/modulfUiSelectors";
import {
	CREATE_ZAHLUNGSVORGANG_EPAYBL,
	GET_ZAHLUNGSVORGANGSTATUS_EPAYBL
} from "../../../utilities/variables/customEventNames";
import { EPAYBL_ZAHLUNGSVORGANG_PATH } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { executeWithoutValidation, validateActivityForSave } from "../../../utilities/validationUtils";
import { executeBusy, reloadTaskAndAntragActivity } from "../../../utilities/workflowUtils";

import {
	buildUpdatedDocument,
	formatDocumentForRequest,
	getNumberValue,
	getStringValue,
	normalizeA12Path,
	ZAHLUNGSSTATUS_BEZAHLT,
	ZAHLUNGSSTATUS_WARTEND
} from "../utils";

import Exception = JsonRpc2Response.Exception;

const ZAHLUNGSVORGANG_EPAYBL_KEYS = RESOURCE_KEYS.features.zahlungsvorgangEpaybl;
const logger = LoggerFactory.getLogger("modulf");

export interface EpayblSetupContext {
	activityId: string;
	documentModel: DocumentModel;
	currentData: FormActivity.Data.SingleDocumentData;
	paymentGroupPath: string;
	document: Activity.Data.Document;
}

type EventNameMatcher = (eventName: string) => boolean;
type EpayblButtonHandler = (context: EpayblSetupContext) => SagaIterator<Activity.Data.Document | undefined>;
type EpayblButtonOptions = { saveBeforeHandling: boolean };

function createButtonEventMatcher(eventNameMatcher: EventNameMatcher) {
	return (action: AnyAction) => {
		return (
			FormEngineActions.event.match(action) &&
			Events.eventButton.match(action.payload.engineEvent) &&
			eventNameMatcher(action.payload.engineEvent.payload.name)
		);
	};
}

export function* setupEpayblContext(
	action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonPayload>>>
): SagaIterator<EpayblSetupContext | undefined> {
	const { activityId } = action.payload;
	const engineState = yield* select(FormEngineSelectors.engineState(activityId));
	assertNonNullable(engineState);
	const documentModel = ModelSelectors.documentModel()(engineState);

	const currentData = yield* select(ActivitySelectors.data(activityId));
	if (!FormActivity.Data.SingleDocumentData.isInstance(currentData)) {
		return undefined;
	}

	const { buttonPath } = action.payload.engineEvent.payload;
	const annotations = yield* select(ModulfUiSelectors.selectButtonAnnotations(activityId, buttonPath));
	const paymentGroupPathWithModelPrefix = annotations.find(it => it.name === EPAYBL_ZAHLUNGSVORGANG_PATH)?.value;
	const paymentGroupPath = normalizeA12Path(paymentGroupPathWithModelPrefix);
	if (!paymentGroupPath) {
		return undefined;
	}

	const document = currentData.document;
	return {
		activityId,
		documentModel,
		currentData,
		paymentGroupPath,
		document
	};
}

function* handleEpayblButtonEvent(
	eventNameMatcher: EventNameMatcher,
	handler: EpayblButtonHandler,
	options: EpayblButtonOptions
): SagaIterator<void> {
	yield* takeLatest(
		createButtonEventMatcher(eventNameMatcher),
		function* (action: Action<FormEngineActions.FormEngineEventActions<Action<Events.EventButtonPayload>>>) {
			yield* call(executeBusy, function* () {
				const { activityId } = action.payload;
				const group = `zahlungsvorgangEpaybl:${activityId}`;

				// Speicher-Validierung wie beim regulären Speichern-Button (siehe customSaveSaga): Pflichtfelder,
				// die erst später im Prozess (z.B. Genehmigung/Ablehnung) benötigt werden, müssen dafür noch
				// nicht gefüllt sein - das Dokument muss lediglich speicherbar sein.
				const isValid = yield* call(validateActivityForSave, activityId);
				if (!isValid) {
					// Ungültige Felder werden bereits inline durch die Validierung im Formular
					// markiert (nutzerfreundlicher als eine zusätzliche generische Fehlermeldung).
					logger.error(
						"Document is not valid, activityId=%s, eventName=%s",
						activityId,
						action.payload.engineEvent.payload.name
					);
					return;
				}

				if (options.saveBeforeHandling) {
					const isSaved = yield* call(saveActivity, activityId);
					if (!isSaved) {
						yield* call(
							putErrorNotification,
							{ key: ZAHLUNGSVORGANG_EPAYBL_KEYS.error.saveFailed },
							"Could not save activity " + activityId,
							activityId,
							group
						);
						return;
					}
				}

				yield* call(executeWithoutValidation, activityId, function* () {
					const context = yield* call(setupEpayblContext, action);
					if (!context) {
						return;
					}

					const updatedDocument = yield* call(handler, context);
					if (!updatedDocument) {
						return;
					}

					yield* put(
						ActivityActions.setData({
							activityId: context.activityId,
							data: { ...context.currentData, document: updatedDocument }
						})
					);

					// Reload the persisted task/antrag state after the payment operation so the client
					// does not keep stale pre-payment values for immutable fields. Otherwise, a later
					// save can try to rewrite those server-generated values and trigger
					// immutable.field.modified.
					yield* call(reloadTaskAndAntragActivity);
				});
			});
		}
	);
}

export function* createZahlungsvorgangEpayblSaga(): SagaIterator<void> {
	yield* call(
		handleEpayblButtonEvent,
		(name: string) => name === CREATE_ZAHLUNGSVORGANG_EPAYBL,
		createZahlungsvorgangEpaybl,
		{ saveBeforeHandling: false }
	);
}

/**
 * Legt über {@code MODULF_START_EPAYMENT} einen Zahlungsvorgang für den berechneten Betrag an.
 * Die Kontierungsinformationen für die Zugehörigkeit des Vorgangs werden serverseitig aus dem
 * Fachverfahren-Manifest aufgelöst (siehe {@code EPaymentService#resolveEPaymentRequest}).
 */
export function* createZahlungsvorgangEpaybl(
	context: EpayblSetupContext
): SagaIterator<Activity.Data.Document | undefined> {
	const { activityId, documentModel, paymentGroupPath, document } = context;

	const price = getNumberValue(document, `${paymentGroupPath}/epaymentBetragFinal`);
	if (price === undefined) {
		yield* call(
			putErrorNotification,
			{ key: ZAHLUNGSVORGANG_EPAYBL_KEYS.error.missingValue },
			{},
			activityId,
			"zahlungsvorgangEpaybl"
		);
		return undefined;
	}

	const request: EPaymentStartRequestDto = { price };

	try {
		const updatedDocumentSpec = yield* call(
			startEPayment,
			document.id,
			formatDocumentForRequest(document, documentModel),
			paymentGroupPath,
			request
		);

		return buildUpdatedDocument(updatedDocumentSpec, documentModel, document.id);
	} catch (error) {
		yield* call(handleEpayblRequestError, error, activityId);
		return undefined;
	}
}

export function* fetchZahlungsstatusEpayblSaga(): SagaIterator<void> {
	yield* call(
		handleEpayblButtonEvent,
		(name: string) => name === GET_ZAHLUNGSVORGANGSTATUS_EPAYBL,
		fetchZahlungsstatusEpaybl,
		{ saveBeforeHandling: true }
	);
}

/**
 * Fragt den Status eines bereits angelegten Zahlungsvorgangs ab. Solange der Vorgang nicht
 * {@code wartend} ist oder keine Payment-ID vorliegt, gibt es nichts abzufragen.
 */
export function* fetchZahlungsstatusEpaybl(
	context: EpayblSetupContext
): SagaIterator<Activity.Data.Document | undefined> {
	const { activityId, documentModel, paymentGroupPath, document } = context;

	const status = getStringValue(document, `${paymentGroupPath}/anlageInformationen/zahlungsstatus`);
	if (status !== ZAHLUNGSSTATUS_WARTEND) {
		return undefined;
	}

	const paymentKey = getStringValue(document, `${paymentGroupPath}/anlageInformationen/paymentId`);
	if (!paymentKey) {
		return undefined;
	}

	try {
		const updatedDocumentSpec = yield* call(getEPaymentStatus, document.id, paymentGroupPath, paymentKey);
		const updatedDocument = buildUpdatedDocument(updatedDocumentSpec, documentModel, document.id);

		if (
			getStringValue(updatedDocument, `${paymentGroupPath}/anlageInformationen/zahlungsstatus`) ===
			ZAHLUNGSSTATUS_BEZAHLT
		) {
			yield* call(
				putSuccessNotification,
				{ key: ZAHLUNGSVORGANG_EPAYBL_KEYS.success.statusUpdated },
				activityId,
				"zahlungsvorgangEpaybl"
			);
		}

		return updatedDocument;
	} catch (error) {
		yield* call(handleEpayblRequestError, error, activityId);
		return undefined;
	}
}

/**
 * Behandelt einen fehlgeschlagenen ePayment-RPC-Aufruf (Anlegen/Statusabfrage): loggt den Fehler
 * und zeigt eine (bestmöglich fachlich angereicherte) Fehlermeldung im Frontend an, statt die
 * Exception unbehandelt aus der Saga propagieren zu lassen (siehe z.B.
 * {@code refreshOpenRegisterRecordSaga.ts} für das gleiche Muster).
 */
function* handleEpayblRequestError(error: unknown, activityId: string): SagaIterator<void> {
	const message: Localizable = isJsonRpc2ResponseArray(error)
		? extractExceptionMessage(error)
		: {
				key: ZAHLUNGSVORGANG_EPAYBL_KEYS.error.requestFailed,
				args: {
					reason: {
						type: "localizable",
						properties: [{ key: ZAHLUNGSVORGANG_EPAYBL_KEYS.error.unbekannt }],
						value: undefined
					}
				}
			};

	yield* call(putErrorNotification, message, error, activityId, "zahlungsvorgangEpaybl");
}

function extractExceptionMessage(response: JsonRpc2Response[]): Localizable {
	const description = response
		.filter(JsonRpc2Response.error.isInstance)
		.map(it => it.error.data)
		.filter(Exception.isInstance)
		.map(it => it.description)
		.pop();
	return {
		key: ZAHLUNGSVORGANG_EPAYBL_KEYS.error.requestFailed,
		args: {
			reason: description?.default
				? { type: "plain", value: description.default }
				: {
						type: "localizable",
						properties: [{ key: ZAHLUNGSVORGANG_EPAYBL_KEYS.error.unbekannt }],
						value: undefined
					}
		}
	};
}
